import type { StaffBlockWire } from "@loot-os/functions/src/dashboard-api/trpc/routers/liveBreakWire.js";
import { Badge } from "@loot-os/ui/components/badge";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { DataTable } from "@loot-os/ui/components/data-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";

import { ErrorAlert } from "../components/ErrorAlert";
import { BlockShopperDialog, UnblockButton } from "../components/LiveBreakBlockControls";
import { ShortDate } from "../components/ShortDate";
import { endUserLabel, type LiveBreakBlocksSearch, staffEndUserLabel } from "../lib/liveBreakStaff";
import { allowerLabel } from "../lib/liveBreaks";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

type Block = StaffBlockWire;

const blockColumns = createDataTableColumns<Block>();
const PINNED = { start: [], end: ["actions"] };

function buildColumns(tenantId: string, canEdit: boolean) {
  const columns = [
    blockColumns.accessor((block) => staffEndUserLabel(block.endUser), {
      id: "endUser",
      header: "Blocked shopper",
      cell: ({ getValue, row }) => (
        <Link
          to="/tenants/$tenantId/users/$userId"
          params={{ tenantId, userId: row.original.endUser.endUserId }}
          className="font-medium hover:underline"
        >
          {getValue()}
        </Link>
      ),
      meta: { filter: "text" },
    }),
    blockColumns.accessor(
      (block) => (block.host ? staffEndUserLabel(block.host) : "House live breaks"),
      {
        id: "host",
        header: "Kept out of",
        cell: ({ getValue, row }) =>
          row.original.host ? (
            <Link
              to="/tenants/$tenantId/storefront/live-breaks/hosts/$userId"
              params={{ tenantId, userId: row.original.host.endUserId }}
              className="hover:underline"
            >
              {`${getValue()}'s live breaks`}
            </Link>
          ) : (
            <Badge variant="secondary">{getValue()}</Badge>
          ),
        meta: { filter: "text" },
      },
    ),
    blockColumns.accessor(
      (block) =>
        block.blockedBy === "host"
          ? "The host"
          : `Staff${block.blockedByStaff ? ` · ${allowerLabel(block.blockedByStaff)}` : ""}`,
      {
        id: "blockedBy",
        header: "Blocked by",
        meta: { filter: "text" },
      },
    ),
    blockColumns.accessor("createdAt", {
      header: "Blocked",
      cell: ({ getValue }) => <ShortDate value={getValue()} />,
    }),
  ];
  if (!canEdit) return blockColumns.columns(columns);
  return blockColumns.columns([
    ...columns,
    blockColumns.display({
      id: "actions",
      size: 104,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <UnblockButton block={row.original} />
        </div>
      ),
    }),
  ]);
}

/** The scope select's values: every Block, the house set, or one host's. */
type ScopeChoice = "all" | "house" | `host:${string}`;

function scopeChoiceOf(search: LiveBreakBlocksSearch): ScopeChoice {
  if (search.scope === "house") return "house";
  if (search.scope === "host" && search.host) return `host:${search.host}`;
  return "all";
}

/**
 * Blocks (boxed's live-break blocks): shoppers a host keeps out of every live break of theirs,
 * and the one set every house live break shares. A Block releases the shopper's queued entries in
 * the live breaks it covers, holds given back. Any member reads; admins block and unblock, on a
 * host's behalf or in the house set.
 */
export function LiveBreakBlocksPage() {
  const { tenantId } = useParams({ from: "/app/tenants/$tenantId/storefront/live-breaks/blocks" });
  const search = useSearch({ from: "/app/tenants/$tenantId/storefront/live-breaks/blocks" });
  const navigate = useNavigate({ from: "/tenants/$tenantId/storefront/live-breaks/blocks" });
  const trpc = useTRPC();
  const { can } = useTenantRole();
  const canEdit = can("admin");
  const choice = scopeChoiceOf(search);
  // undefined = every Block; null = the house set; a string = that host's.
  const hostEndUserId =
    choice === "all" ? undefined : choice === "house" ? null : choice.slice("host:".length);

  const blocks = useQuery(
    trpc.liveBreaks.blocks.list.queryOptions(hostEndUserId === undefined ? {} : { hostEndUserId }),
  );
  const hosts = useQuery(trpc.liveBreaks.hosts.list.queryOptions({ filter: "all", limit: 100 }));
  const rows = useMemo(() => blocks.data?.blocks ?? [], [blocks.data]);
  const columns = useMemo(() => buildColumns(tenantId, canEdit), [tenantId, canEdit]);

  const hostOptions = useMemo(() => {
    const options: Record<string, string> = {};
    for (const host of hosts.data?.hosts ?? []) {
      options[`host:${host.endUserId}`] = endUserLabel(host);
    }
    // A host from a link who is past the first page still names a row.
    if (choice.startsWith("host:") && !(choice in options)) options[choice] = choice.slice(5);
    return options;
  }, [hosts.data, choice]);
  const scopeItems: Record<string, string> = {
    all: "Every Block",
    house: "House live breaks",
    ...hostOptions,
  };
  const blockedIds = new Set(rows.map((block) => block.endUser.endUserId));

  const setChoice = (next: string) => {
    const search: LiveBreakBlocksSearch =
      next === "house"
        ? { scope: "house" }
        : next.startsWith("host:")
          ? { scope: "host", host: next.slice("host:".length) }
          : {};
    void navigate({ search, replace: true });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-semibold tracking-tight">Blocks</h1>
          <p className="text-sm text-muted-foreground">
            Shoppers kept out of a host's live breaks, open or future, and the one set every house
            live break shares. A Block releases the shopper's queued entries there and gives their
            holds back; they can no longer buy, chat or watch.
          </p>
        </div>
        {canEdit && hostEndUserId !== undefined && (
          <BlockShopperDialog
            hostEndUserId={hostEndUserId}
            setName={hostEndUserId === null ? "house live breaks" : (scopeItems[choice] ?? "")}
            blockedIds={blockedIds}
          />
        )}
      </div>

      {blocks.error ? (
        <ErrorAlert message={blocks.error.message} />
      ) : (
        <Card>
          <CardContent className="flex flex-col gap-3">
            {/* Above the table rather than in its toolbar, whose trailing group never wraps. */}
            <div className="flex flex-wrap items-center gap-2">
              <Select items={scopeItems} value={choice} onValueChange={(v) => v && setChoice(v)}>
                <SelectTrigger aria-label="Whose Blocks" className="w-52" size="sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Every Block</SelectItem>
                  <SelectItem value="house">House live breaks</SelectItem>
                  {Object.entries(hostOptions).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {blocks.isPending ? (
              <Skeleton className="h-40" />
            ) : (
              <DataTable
                columns={columns}
                data={rows}
                getRowId={(block) => block.id}
                pinned={canEdit ? PINNED : undefined}
                search="Search blocks"
                csvName="live-break-blocks"
                emptyMessage={
                  hostEndUserId === undefined
                    ? "No one is blocked from a live break."
                    : hostEndUserId === null
                      ? "No one is blocked from the house live breaks."
                      : "This host blocks no one."
                }
              />
            )}
          </CardContent>
        </Card>
      )}
      {canEdit && hostEndUserId === undefined && (
        <p className="text-sm text-muted-foreground">
          Choose the house live breaks or a host above to block a shopper there.
        </p>
      )}
    </div>
  );
}
